import React from "react";
import Cta from "@qlik/qlik/src/components/Cta";
import Media from "@qlik/qlik/src/components/Media";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";

/**
 * HorizontalCTAImage is a component that displays a horizontal call to action with an image.
 * It takes a data object as a prop which contains all the necessary information to render the component.
 *
 * @param {Object} data - The data object containing the information to render the component.
 * @returns {JSX.Element} The HorizontalCTAImage component.
 */
const HorizontalCTAImage = ({ data }) => {
    const { cardItem, backgroundImage, additionalConfiguration, customId } = data ?? {};
    const { title, cta, media, eyebrowText, description } = cardItem ?? {};

    // Getting the spacing class name based on the additional configuration.
    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration);

    return (
        // The main section of the component. It has a background color of ocean and a padding on the y-axis.
        <section className={`flex items-center bg-ocean py-15 md:py-17 ${sectionSpacingClassName}`} id={customId}>
            <div className="container relative">
                {/* If there is a background image, it is rendered here. */}
                {backgroundImage?.length > 0 && backgroundImage?.[0]?.resource_type === "image" && (
                    <div className="absolute -left-[700px] top-[87px]">
                        <Media
                            link={backgroundImage?.[0]?.secure_url}
                            width={backgroundImage?.[0]?.width}
                            height={backgroundImage?.[0]?.height}
                            className="w-[1228px] h-[282px]"
                        />
                    </div>
                )}
                <div className="row items-center relative z-2">
                    <div className="col-12 md:col-5">
                        {/* If there is a media item, it is rendered here. */}
                        {media?.[0] && (
                            <div className="flex justify-center lg:justify-start">
                                <Media media={media?.[0]} className="w-65.25 h-full lg:w-82.5 shadow-xxxl" />
                            </div>
                        )}
                    </div>
                    <div className="col-12 md:col-7 text-center md:text-left">
                        <div className="flex justify-center flex-col pt-15 pb-0 md:py-15">
                            {/* If there is an eyebrow text, it is rendered here. */}
                            {eyebrowText && (
                                <p className="text-white text-base tracking-wide5 font-medium mb-4.25">{eyebrowText}</p>
                            )}
                            {/* If there is a title, it is rendered here. */}
                            {title && <h2 className="text-white">{title}</h2>}
                            {/* If there is a description, it is rendered here. */}
                            {description?.description && (
                                <p className="text-white text-2xl leading-relaxed font-light tracking-tight6 mt-5">
                                    {description.description}
                                </p>
                            )}
                            {cta && cta?.length > 0 && (
                                <div className="mt-15">
                                    {/* If there are CTAs, they are rendered here. */}
                                    {cta?.map((button) => (
                                        <div className="flex gap-2 justify-center md:justify-start" key={button.id}>
                                            <Cta key={button.id} data={button} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HorizontalCTAImage;
